import moment from 'moment'

export const translationMap = {
  sale: 'residentialStatus.sale',
  postal: 'postcode',
  reserved: 'residentialStatus.reserved',
  sold: 'residentialStatus.sold',
  soldAt: 'residentialStatus.sold',
  priceTotal: 'totalPrice',
  count: 'quantity',
  storage: 'propertyType:storage',
  flat: 'propertyType:flat',
  semidetached: 'propertyType:semidetached',
  detached: 'propertyType:detached',
  fourPlex: 'propertyType:fourPlex',
  chainLinkedHouse: 'propertyType:chainLinkedHouse',
  boathouse: 'propertyType:boathouse',
  boatBerth: 'propertyType:boatBerth',
  plot: 'propertyType:plot',
  leisure: 'propertyType:leisure',
  leisurePlot: 'propertyType:leisurePlot',
  terraced: 'propertyType:terraced',
  housingType: 'numberOfBedrooms',
  unknown: 'dataUnknown',
  averageSquarePrice: 'residentialAverageSquarePrice',
  property: 'unit',
  groups: 'group',
  owners: 'owner',
  draft: 'residentialStatus.draft',
}

export const validHousingTypes = ['flat', 'detached', 'semidetached', 'leisure', 'terraced']

export const RemoveSize = x => x.split(':')[0]

export const onlyUnknowns = (data = {}) => {
  const keys = Object.keys(data)
  return keys.length === 1 && keys[0] === 'unknown'
}

export const propertyValue = ({ dimension, item, valueParser }) => {
  if (dimension === 'housingType') {
    if (!validHousingTypes.includes(item.propertyType)) {
      return '-'
    }
    return valueParser(item[dimension], { type: 'housingType' })
  }
  return valueParser(item[dimension])
}

export const getPropertyItems = ({ data, status }) => {
  const items = data.values ? data.values.find(value => value.status === status) || { items: [] } : { items: [] }

  return items.items.map(v => {
    !validHousingTypes.includes(v.propertyType) ? (v.housingType = '') : v.housingType
    return v
  })
}

export const getWidgets = ({ widgets, sources, grid }) => {
  const widgetTypes = Object.keys(widgets)
  const validWidgets = sources.filter(source => widgetTypes.includes(source.type))

  const gridItems = Array.from(
    new Set(
      grid
        .split(' ')
        .filter(x => !['|', '.'].includes(x))
        .map(RemoveSize),
    ),
  )

  return gridItems
    .map(item => {
      const widget = validWidgets.find(widget => widget.key === item)
      if (widget) return widget
      return null
    })
    .filter(Boolean)
}

export const createGrid = gridArea => {
  const templateArea = gridArea
    .split('|')
    .filter(x => x.trim())
    .map(x => {
      return `"${x.split(' ').map(RemoveSize).join(' ')}"`
    })
    .join('\n')

  const templateColumns = gridArea
    .split('|')[0]
    .trim()
    .split(' ')
    .map(x => {
      let [, size = '1fr'] = x.split(':')

      return size
    })
    .join(' ')

  return { templateArea, templateColumns }
}

export const getColors = (keys = [], asObject = false) => {
  if (asObject) {
    return Object.fromEntries(
      Object.entries(colors)
        .map(([key, value]) => {
          if (keys.find(label => key === label)) return [key, value]
        })
        .filter(Boolean),
    )
  }
  return Object.entries(colors)
    .map(([key, value]) => {
      if (keys.find(label => key === label)) return value
    })
    .filter(Boolean)
}

export const getCssColor = (key, opacity = 1) => {
  let [h, s, l] = ['h', 's', 'l'].map(k => getComputedStyle(document.documentElement).getPropertyValue(`--${key}-${k}`))

  return `hsla(${h}, ${s}, ${l}, ${opacity})`
}

export function isValidDate(dateString) {
  return !isNaN(Date.parse(dateString))
}

export const ValueTypes = {
  translate: 'translate',
  date: 'date',
  currency: 'currency',
  propertyType: 'housingType',
  number: 'number',
  string: 'string',
}

export const defaultColors = {
  blue900: '#000112',
  blue800: '#000437',
  blue700: '#00075b',
  blue600: '#000a80',
  blue500: '#000da4',
  blue400: '#0010c8',
  blue300: '#0012ed',
  blue200: '#1225ff',
  blue100: '#3746ff',
  grey900: '#1b1b1c',
  grey800: '#2d2d2e',
  grey700: '#3f3f41',
  grey600: '#505054',
  grey500: '#747479',
  grey400: '#86868b',
  grey300: '#99999d',
  grey200: '#ababaf',
  grey100: '#bebec0',
}

export const DefaultDoughnutColors = {
  color1: '#7C2A26',
  color2: '#81A0EE',
  color3: '#D37468',
  color4: '#2A3793',
  color5: '#DE7449',
  color6: '#B5DCFF',
  color7: '#01095A',
  color8: '#D4D5E9',
}

export const colors = {
  default: '#000000',
  reserved: '#FFB82F',
  sold: '#1AE79D',
  sale: '#FD3062',
  flat: DefaultDoughnutColors.color1,
  detached: DefaultDoughnutColors.color4,
  unknown: '#e1e1e1',
  terraced: DefaultDoughnutColors.color2,
  storage: DefaultDoughnutColors.color7,
  semidetached: DefaultDoughnutColors.color3,
  leisure: DefaultDoughnutColors.color5,
  office: DefaultDoughnutColors.color8,
  plot: defaultColors['blue200'],
  leisurePlot: DefaultDoughnutColors.color2,
  ...defaultColors,
}

export const getDiffCurrentYearToDate = () => {
  const date = new Date()
  date.setMonth(0)
  date.setDate(1)
  const diff = moment().diff(date, 'days', true)
  return { date: moment().subtract(diff, 'days').format('YYYY-MM-DD'), value: diff }
}

export const getFormattedNumber = (value, options = {}) => {
  const { notation, maximumSignificantDigits, language, currency } = options
  if (currency)
    return new Intl.NumberFormat(language || 'nb', {
      notation,
      style: 'currency',
      maximumSignificantDigits: notation === 'compact' ? 4 : maximumSignificantDigits,
      currency,
    }).format(value)
  return new Intl.NumberFormat(language || 'nb', {
    notation,
    maximumSignificantDigits: notation === 'compact' ? 4 : maximumSignificantDigits,
  }).format(value)
}

export const getDiffCurrentMonthToDate = () => {
  const date = new Date()
  date.setDate(1)
  const diff = moment().diff(date, 'days', true)

  return { date: moment().subtract(diff, 'days').format('YYYY-MM-DD'), value: diff }
}

export const defaultLinearFilters = [
  { value: 7, label: 'filterLastWeek' },
  { value: 30, label: 'filterLastThirty' },
  { value: 60, label: 'filterLastSixty' },
  { value: 90, label: 'filterLastNinty' },
  { value: 180, label: 'filterLastHalfYear' },
  { value: 270, label: 'filterLastThreeQuarter' },
  { value: 360, label: 'filterLastYear' },
  {
    value: [180, 270, 360].includes(getDiffCurrentYearToDate().value)
      ? getDiffCurrentYearToDate().value + 1
      : getDiffCurrentYearToDate().value,
    label: 'filterStartCurrentYearToday',
  },
  {
    value: [7, 30].includes(getDiffCurrentMonthToDate().value)
      ? getDiffCurrentMonthToDate().value + 1
      : getDiffCurrentMonthToDate().value,
    label: 'filterStartCurrentMonthToday',
  },
  { value: '*', label: 'filterFromProjectStart' },
]

export const defaultFilterKeys = {
  7: moment().subtract(7, 'days').format('YYYY-MM-DD'),
  30: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  60: moment().subtract(60, 'days').format('YYYY-MM-DD'),
  90: moment().subtract(90, 'days').format('YYYY-MM-DD'),
  180: moment().subtract(180, 'days').format('YYYY-MM-DD'),
  270: moment().subtract(270, 'days').format('YYYY-MM-DD'),
  360: moment().subtract(360, 'days').format('YYYY-MM-DD'),
  [getDiffCurrentMonthToDate().value]: getDiffCurrentMonthToDate().date,
  [getDiffCurrentYearToDate().value]: getDiffCurrentYearToDate().date,
  '*': '*',
}

export const defaultDougnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
}

export const getGroupedData = (data, key) => {
  const { values = {} } = data
  const item = values[key]
  const itemValues = item && item.values ? item.values : []
  if (!item || !itemValues.length) return 0
  if (!(itemValues.length > 1)) return itemValues[0].count
  return itemValues.reduce((sum, curr) => sum + curr.count, 0)
}

export const defaultLinearOptions = ({ source }) => ({
  maintainAspectRatio: false,
  responsive: true,
  interaction: {
    intersect: false,
    mode: 'nearest',
  },
  // onClick: e => {
  //   const { y, chart } = e
  //   const dataY = chart.scales.y.getValueForPixel(y)
  //   console.log(dataY)
  // },
  elements: {
    point: { radius: 6, borderWidth: 2 },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          let label = context.dataset.label || ''

          if (label) {
            label += ': '
          }
          if (context.parsed.y !== null) {
            label += ` ${context.parsed.y}`
          }
          return label
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        display: true,
      },
      ticks: {
        callback: value => {
          if (!source.divider) {
            return Number(value) ? (Number.isInteger(value) ? value : '') : value
          }
          return value + source.divider.suffix
        },
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
})

export const defaultDougnutDatasetOptions = {
  cutout: '60%',
}

export const defaultLinearDatasetOptions = {
  fill: false,
  cubicInterpolationMode: 'monotone',
}

export const today = () => {
  return moment().format('YYYY-MM-DD')
}

export const getFormatedDate = date => {
  return moment(date).format('DD.MM.YY')
}
